import { apiRequest } from "./api.client";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PlanInfo {
    plan_id: string;
    name: string;
    features: string[];
    tier: string;
    interval: string;
    /** Taxable value, i.e. the price as quoted. Not what Checkout charges. */
    price_paise: number;
    currency: string;
    annual_savings_pct: number;
    requires_card: boolean;
    is_trial: boolean;
    trial_eligible: boolean;
    trial_days: number | null;
    /** GST added on top of price_paise for subscriptions. */
    gst_paise:           number;
    gst_rate_pct:        number;
    /** What Razorpay actually charges. Show this at the point of purchase. */
    total_payable_paise: number;
}

export interface FeatureFlag {
    feature: string;
    is_enabled: boolean;
    expires_at: string | null;
    is_trial: boolean;
}

export interface SubscriptionStatus {
    has_subscription: boolean;
    plan_id: string | null;
    plan_name: string | null;
    /** Resolved from PLAN_METADATA server-side. Prefer this for anything shown
     *  to a user: it never carries a raw Razorpay plan id. */
    display_name: string | null;
    /** "base" | "premium". Null when the plan is not in this environment's
     *  PLAN_METADATA. Key styling off this, never off the display string. */
    tier: string | null;
    /** "monthly" | "annual" | "trial". Null under the same condition as tier. */
    interval: string | null;
    status: string | null; // active | cancelled_scheduled | expired | halted | pending | none
    is_trial: boolean;
    features: string[];
    feature_flags: FeatureFlag[];
    current_period_start: string | null;
    current_period_end: string | null;
    last_receipt_url: string | null;
    cancelled_at: string | null;
    trial_end_at: string | null;
    payment_attempt_count: number;
    next_charge_at: string | null;
    next_charge_paise: number | null;
    update_payment_method_url: string | null;
}

export interface SubscribeResponse {
    subscription_id: string;
    plan_id: string;
    razorpay_key_id: string;
    short_url: string | null;
    expects_card: boolean;
}

export interface CancelResponse {
    status: string;
    cancelled_at: string;
    current_period_end: string | null;
    message: string;
}

export interface ReactivateResponse {
    status: string;
    message: string;
}

export interface CustomerPortalResponse {
    url: string | null;
    message: string;
}

export interface UpgradeQuote {
    order_id: string;
    amount_paise: number;
    credit_paise: number;
    razorpay_key_id: string;
    from_plan_name: string;
    to_plan_name: string;
}

// ── API ────────────────────────────────────────────────────────────────────────

export const BillingAPI = {
    getSubscription: () => apiRequest<SubscriptionStatus>("/billing/subscription"),
    getPlans: () => apiRequest<PlanInfo[]>("/billing/plans"),
    getFeatureFlags: () => apiRequest<FeatureFlag[]>("/clinic/feature-flags"),
    subscribe: (planId: string) =>
        apiRequest<SubscribeResponse>("/billing/subscribe", {
            method: "POST",
            body: JSON.stringify({ plan_id: planId }),
        }),
    requestUpgrade: (targetPlanId: string) =>
        apiRequest<UpgradeQuote>("/billing/upgrade", {
            method: "POST",
            body: JSON.stringify({ target_plan_id: targetPlanId }),
        }),
    cancelSubscription: () =>
        apiRequest<CancelResponse>("/billing/cancel", { method: "POST" }),
    reactivateSubscription: () =>
        apiRequest<ReactivateResponse>("/billing/reactivate", { method: "POST" }),
    getCustomerPortalUrl: () =>
        apiRequest<CustomerPortalResponse>("/billing/customer-portal-url"),
    confirmTrial: () =>
        apiRequest<SubscriptionStatus>("/billing/confirm-trial", { method: "POST" }),
};

// ── Kaagazy tax invoices (our own billing to the clinic) ─────────────────────
// Distinct from the clinic's patient invoices in invoice.service.ts: these are
// the GST documents Kaagazy issues for subscription charges and credit top-ups.

export interface PlatformInvoice {
    id:               string;
    invoice_number:   string;
    kind:             "invoice" | "credit_note";
    reason:           "subscription" | "credit_topup";
    issued_at:        string;
    clinic_gstin:     string | null;
    place_of_supply:  string | null;
    base_paise:       number;
    cgst_paise:       number;
    sgst_paise:       number;
    igst_paise:       number;
    total_paise:      number;
    gst_rate_pct:     number;
    sac_code:         string;
    /** False while the PDF is still rendering, or if rendering failed. */
    pdf_available:    boolean;
}

export interface PlatformInvoicePdf {
    url:                string;
    expires_in_seconds: number;
}

export const PlatformInvoiceAPI = {
    list: () => apiRequest<PlatformInvoice[]>("/platform-invoices"),
    getPdfUrl: (invoiceId: string) =>
        apiRequest<PlatformInvoicePdf>(`/platform-invoices/${invoiceId}/pdf`),
};
